// BEAM UI primitive — Dropdown.
// A modal-list single select. Same SelectOption shape as Select (chip group), but
// for option sets a chip row would overwhelm (e.g. Job Type, Customer) — the trigger
// shows the selected label (or a placeholder) and tapping it opens a bottom-sheet Modal
// with a scrollable list of rows. Picking a row calls onChange and closes the list.
// The sheet mirrors GlobalAssistantDock: a definite-height root so maxHeight resolves,
// a safe-area bottom inset, and the keyboard dismissed on open, so the rows are never
// pushed off the bottom edge or hidden behind the keyboard/nav bar. Theme tokens only.
import React, { useState } from 'react';
import { Keyboard, Modal, Pressable, ScrollView, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { theme } from '../../theme';
import { Text } from './Text';
import type { SelectOption } from './Select';

type DropdownProps<T extends string> = {
  label?: string;
  options: ReadonlyArray<SelectOption<T>>;
  value: T | null;
  onChange: (value: T) => void;
  placeholder?: string;
  style?: StyleProp<ViewStyle>;
};

export function Dropdown<T extends string>({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select…',
  style,
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const insets = useSafeAreaInsets();
  const selected = options.find((opt) => opt.value === value) ?? null;

  function pick(next: T) {
    onChange(next);
    setOpen(false);
  }

  function openList() {
    // A picker has no text input of its own; drop any open keyboard so the bottom
    // sheet is not rendered behind it (real-device occlusion the emulator hid).
    Keyboard.dismiss();
    setOpen(true);
  }

  return (
    <View style={[styles.container, style]}>
      {label ? (
        <Text variant="small" color="secondary">
          {label}
        </Text>
      ) : null}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={selected ? selected.label : placeholder}
        onPress={openList}
        style={({ pressed }) => [styles.trigger, pressed && styles.triggerPressed]}
      >
        <Text variant="body" color={selected ? 'primary' : 'secondary'} numberOfLines={1} style={styles.triggerText}>
          {selected ? selected.label : placeholder}
        </Text>
        <Text variant="small" color="secondary">
          ▼
        </Text>
      </Pressable>

      <Modal visible={open} transparent animationType="slide" onRequestClose={() => setOpen(false)}>
        {/* Full-screen container with a definite height so the sheet's maxHeight resolves
            and it anchors to the bottom (mirrors the working GlobalAssistantDock sheet). */}
        <View style={styles.modalRoot}>
          <Pressable
            style={styles.scrim}
            accessibilityRole="button"
            accessibilityLabel="Close"
            onPress={() => setOpen(false)}
          />
          <View style={[styles.sheet, { paddingBottom: insets.bottom + theme.spacing.md }]}>
            {label ? (
              <Text variant="subhead" numberOfLines={1}>
                {label}
              </Text>
            ) : null}
            <ScrollView keyboardShouldPersistTaps="handled" style={styles.list}>
              {options.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <Pressable
                    key={opt.value}
                    accessibilityRole="button"
                    accessibilityLabel={opt.label}
                    accessibilityState={{ selected: isSelected }}
                    onPress={() => pick(opt.value)}
                    style={({ pressed }) => [
                      styles.row,
                      isSelected && styles.rowSelected,
                      pressed && styles.rowPressed,
                    ]}
                  >
                    <Text variant="body" color={isSelected ? 'link' : 'primary'}>
                      {opt.label}
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: theme.spacing.xs },
  trigger: {
    minHeight: theme.layout.fieldHeight,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.color.bg.surface,
    borderRadius: theme.radius.sm,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.strong,
    paddingHorizontal: theme.spacing.md,
    gap: theme.spacing.sm,
  },
  triggerPressed: { backgroundColor: theme.color.bg.subtle },
  triggerText: { flex: 1 },
  modalRoot: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  scrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: theme.color.scrim,
  },
  sheet: {
    backgroundColor: theme.color.bg.surface,
    borderTopLeftRadius: theme.radius.lg,
    borderTopRightRadius: theme.radius.lg,
    paddingHorizontal: theme.spacing.md,
    paddingTop: theme.spacing.lg,
    gap: theme.spacing.sm,
    maxHeight: '80%',
  },
  list: { flexGrow: 0 },
  row: {
    minHeight: theme.layout.minTapTarget,
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.radius.sm,
  },
  rowSelected: { backgroundColor: theme.color.focusRing },
  rowPressed: { backgroundColor: theme.color.bg.subtle },
});
